/*    
<summary>
   This class component is all about Managing Authentication functionality.
   Developer:Aashish Singh, Created Date:29-March-2023
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import base64 from "base-64";
import { action, computed, makeObservable, observable } from "mobx";
import * as baseService from "../service/base-service";
import { ICommonState } from "../../models/state/ICommonState";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import { IAuthState } from "../../models/state/IAuthState";
import IAuthResponse, {
  ICustomerAuthResponse,
} from "../../models/response/IAuthResponse";
import URLConstants from "../../constants/url-constants";
import ILogin, {
  ICustomerLogin,
  IUserTypeLogin,
} from "../../models/forms/ILogin";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";
import { formatMessage } from "../../translations/format-message";
import { IRoleVM } from "../../models/response/IRoleResponse";
import { PermissionType } from "../../constants/enums/permission-type-enum";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { toHalfWidth } from "../../helpers/validate-helper";
import { IObservableInitialState } from "../../models/ICommon";
import { safeBase64Encode } from "../../constants/constants";

export class AuthStore implements IAuthState, ICommonState {
  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };
  inProgress = false;
  error = "";
  isAuthenticated = false;
  token = "";
  userId: number = 0;
  tenantId: number = 0;
  userType = "";
  email = "";
  loginFormData: ILogin = {
    email: "",
    password: "",
    rememberMe: false,
  };
  logoutSuccess = false;
  logoutError = "";
  logoutInprogress = false;

  showLogoutPopup: boolean = false;
  permissions: Array<IRoleVM> = [];

  generateCustomerAuthCodeState = { ...this.initialStateValue };
  authenticateCustomerAuthCodeState = { ...this.initialStateValue };
  resetPasswordAuthLinkState = { ...this.initialStateValue };
  resetPasswordState = { ...this.initialStateValue };
  isCustomerAuthCodeValidated = false;
  isResetPasswordAuthLinkGenerated = false;

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      tenantId: observable,
      userId: observable,
      isAuthenticated: observable,
      loginFormData: observable,
      logoutSuccess: observable,
      logoutError: observable,
      logoutInprogress: observable,
      permissions: observable,
      showLogoutPopup: observable,
      resetPasswordState: observable,
      isResetPasswordAuthLinkGenerated: observable,
      resetPasswordAuthLinkState: observable,
      isCustomerAuthCodeValidated: observable,
      authenticateCustomerAuthCodeState: observable,
      generateCustomerAuthCodeState: observable,
      GenerateCustomerAuthCodeService: action,
      AuthenticateCustomerAuthCodeService: action,
      setIsCustomerAuthCodeValidatedFalse: action,
      resetAuthenticateCustomerAuthCode: action,
      resetGenerateCustomerAuthCodeState: action,
      resetAuthenticateCustomerAuthCodeError: action,
      AppointmentRegisterCustomerlogin: action,
      ResetPasswordAuthLinkService: action,
      resetResetPassowrdAuthLinkState: action,
      resetIsResetPasswordAuthLinkGenerated: action,
      ResetPasswordService: action,
      resetStatePasswordReset: action,
      reset: action,
      login: action,
      loginAsStoreService: action,
      logout: action,
      updateToken: action,
      Customerlogin: action,
      setShowLogoutPopup: action,
      getUserId: computed,
      getPermissionType: computed,
      getAllPermission: computed,
    });
  }

  get getUserType() {
    if (this.userType) {
      return this.userType;
    }
    return localStorage.getItem("lizera_userType");
  }

  get getPermissionType() {
    return Number(localStorage.getItem("lizera_permission_type"));
  }

  get getToken() {
    return localStorage.getItem("lizera_token");
  }

  get getRefreshToken() {
    return localStorage.getItem("lizera_refreshToken");
  }

  get getEmail() {
    return localStorage.getItem("lizera_email");
  }

  get getUserId() {
    let userId = localStorage.getItem("lizera_userId")
      ? localStorage.getItem("lizera_userId")
      : "0";
    return parseInt(userId!);
  }

  get getAllPermission() {
    if (this.permissions) {
      return [...this.permissions];
    }
    return [];
  }

  /*
    This function is used to reset all observables to their initial values.  
    */

  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.isAuthenticated = false;
    this.logoutError = "";
    this.logoutInprogress = false;
    this.logoutSuccess = false;
    this.userType = "";
    this.loginFormData = {
      email: "",
      password: "",
      rememberMe: false,
    };
  };
  /*
    This function is used to reset Logout observables to their initial values.  
    */

  resetLogout = () => {
    this.logoutError = "";
    this.logoutInprogress = false;
    this.logoutSuccess = false;
  };

  /*
    This function is used to Authenticate User and get token (if authentic) from API.  
    */

  login = (data: ILogin) => {
    this.inProgress = true;
    this.error = "";
    this.loginFormData = JSON.parse(JSON.stringify(data));
    const authData = {
      Email: data.email,
      UserType: data.userType,
      Password: safeBase64Encode(data.password),
    };
    return baseService
      .postRequest(URLConstants.Authenticate, authData)
      .then((response: IApiResponse<IApiSuccessResponse<IAuthResponse>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
        } else {
          let data = response.data.Data;
          let permissions: Array<IRoleVM> = data.Role ? data.Role : [];
          this.permissions = permissions;
          const userToken: any = jwtDecode(data.AccessToken);
          this.userType = userTypeEnum[userToken.userType];
          if (data.BusinessTypeId !== null) {
            localStorage.setItem(
              "lizera_businessTypeId",
              data.BusinessTypeId?.toString()
            );
          }
          if (data.StoreId)
            localStorage.setItem("lizera_store_id", data.StoreId?.toString());
          if (data.StoreCode)
            localStorage.setItem("lizera_store_code", data.StoreCode);
          if (data.HeadquarterId)
            localStorage.setItem(
              "lizera_headquarter_id",
              data.HeadquarterId?.toString()
            );
          localStorage.setItem("lizera_email", userToken.userEmail);
          localStorage.setItem("lizera_userId", data.UserId);
          localStorage.setItem(
            "lizera_userType",
            userTypeEnum[userToken.userType]
          );
          const theme: any = localStorage.getItem("lizera_theme");
          localStorage.setItem("lizera_sidebarClosed", "true");
          localStorage.setItem("lizera_theme", theme ? theme : "0");
          localStorage.setItem("lizera_token", data.AccessToken);
          localStorage.setItem(
            "lizera_permission_type",
            PermissionType[userToken.permissionType]
          );
          localStorage.setItem("lizera_is_access_tenant", "false");
          localStorage.setItem("lizera_access_tenant_id", userToken.tenantId);
          localStorage.setItem("lizera_access_tenant_name", "");
          localStorage.setItem(
            "lizera_permissions",
            JSON.stringify(permissions)
          );
          this.isAuthenticated = true;
          toast.success(formatMessage("login_successfully"));
        }
      })
      .catch((err: string) => {
        this.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  loginAsStoreService = (data: IUserTypeLogin) => {
    this.inProgress = true;
    this.error = "";
    this.loginFormData = JSON.parse(JSON.stringify(data));
    const authData = {
      StoreCode: toHalfWidth(data.storeCode),
      UserType: data.userType,
      Password: safeBase64Encode(data.password),
    };
    return baseService
      .postRequest(URLConstants.Authenticate + "/Store", authData)
      .then((response: IApiResponse<IApiSuccessResponse<IAuthResponse>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
        } else {
          let data = response.data.Data;
          let permissions: Array<IRoleVM> = data.Role ? data.Role : [];
          this.permissions = permissions;
          const userToken: any = jwtDecode(data.AccessToken);
          this.userType = userTypeEnum[userToken.userType];
          if (data.BusinessTypeId !== null) {
            localStorage.setItem(
              "lizera_businessTypeId",
              data.BusinessTypeId?.toString()
            );
          }
          const theme: any = localStorage.getItem("lizera_theme");
          localStorage.setItem("lizera_email", userToken.userEmail);
          localStorage.setItem("lizera_sidebarClosed", "true");
          localStorage.setItem("lizera_theme", theme ? theme : "0");
          localStorage.setItem("lizera_userId", data.UserId);
          if (data.StoreId)
            localStorage.setItem("lizera_store_id", data.StoreId?.toString());
          if (data.StoreCode)
            localStorage.setItem("lizera_store_code", data.StoreCode);
          if (data.HeadquarterId)
            localStorage.setItem(
              "lizera_headquarter_id",
              data.HeadquarterId?.toString()
            );
          localStorage.setItem(
            "lizera_userType",
            userTypeEnum[userToken.userType]
          );
          localStorage.setItem("lizera_token", data.AccessToken);
          localStorage.setItem(
            "lizera_permission_type",
            PermissionType[userToken.permissionType]
          );
          localStorage.setItem("lizera_is_access_tenant", "false");
          localStorage.setItem("lizera_access_tenant_id", userToken.tenantId);
          localStorage.setItem("lizera_access_tenant_name", "");
          localStorage.setItem(
            "lizera_permissions",
            JSON.stringify(permissions)
          );
          this.isAuthenticated = true;
          toast.success(formatMessage("login_successfully"));
        }
      })
      .catch((err: string) => {
        this.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all observables to their initial values and clear local storage.  
    */

  logout = () => {
    this.logoutInprogress = true;
    return baseService
      .postRequest(URLConstants.Logout, {})
      .then((response: IApiResponse<any>) => {
        if (response.data.Error) {
          this.logoutError = response.data.Message;
        } else {
          this.reset();
          const lang: any = localStorage.getItem("lizera_language");
          const theme: any = localStorage.getItem("lizera_theme");
          const userType: any = localStorage.getItem("lizera_userType");
          localStorage.clear();
          localStorage.setItem("lizera_language", lang === "en" ? "en" : "jp");
          localStorage.setItem("lizera_is_access_tenant", "false");
          localStorage.setItem("lizera_sidebarClosed", "true");
          localStorage.setItem("lizera_theme", theme ? theme : "0");
          localStorage.setItem("lizera_access_tenant_id", "0");
          localStorage.setItem("lizera_access_tenant_name", "");
          localStorage.setItem("lizera_userType", userType);
          this.logoutSuccess = true;
          toast.success(formatMessage("logout_successfully"));
        }
      })
      .catch((err: string) => {
        this.logoutError = err;
      })
      .finally(
        action(() => {
          this.logoutInprogress = false;
        })
      );
  };

  updateToken = (data: IAuthResponse) => {
    localStorage.setItem("lizera_token", data.AccessToken);
    // this.token = data.AccessToken;
  };

  setShowLogoutPopup = (flag: boolean) => {
    this.showLogoutPopup = flag;
  };

  Customerlogin = (data: ICustomerLogin) => {
    this.inProgress = true;
    this.error = "";
    this.loginFormData = JSON.parse(JSON.stringify(data));
    const authData = {
      Email: data.email,
      Password: safeBase64Encode(data.password),
    };
    return baseService
      .postRequest(URLConstants.CustomerAuthenticate, authData)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<ICustomerAuthResponse>>
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
          } else {
            let data = response.data.Data;
            let permissions: Array<IRoleVM> = data.Role ? data.Role : [];
            this.permissions = permissions;
            const userToken: any = jwtDecode(data.AccessToken);
            this.userType = userTypeEnum[userToken.userType];
            if (data.BusinessTypeId !== null) {
              localStorage.setItem(
                "lizera_businessTypeId",
                data.BusinessTypeId?.toString()
              );
            }
            // if(userTypeEnum[userToken.userType] === userTypeEnum[userTypeEnum.Customer])
            //   localStorage.setItem("lizera_store_id", userToken.storeId?.toString());
            localStorage.setItem(
              "lizera_permissions",
              JSON.stringify(permissions)
            );
            localStorage.setItem(
              "lizera_userType",
              userTypeEnum[userToken.userType]
            );
            localStorage.setItem("lizera_email", userToken.customerEmail);
            localStorage.setItem("lizera_userId", userToken.customerId);
            localStorage.setItem("lizera_store_id", userToken.storeId);
            localStorage.setItem("lizera_token", data.AccessToken);
            localStorage.setItem(
              "lizera_blocked_stores",
              JSON.stringify(data.StoreIds)
            );
            localStorage.setItem(
              "lizera_permission_type",
              PermissionType[userToken.permissionType]
            );
            localStorage.setItem("lizera_is_access_tenant", "false");
            localStorage.setItem("lizera_access_tenant_id", userToken.tenantId);
            localStorage.setItem("lizera_access_tenant_name", "");
            this.isAuthenticated = true;
            toast.success(formatMessage("login_successfully"));
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

    AppointmentRegisterCustomerlogin = (data: any) => {
    this.inProgress = true;
    this.error = "";
    this.loginFormData = JSON.parse(JSON.stringify(data));
    return baseService
      .postRequest(URLConstants.CustomerAuthenticate, {
        Email: data.Email,
        Password: data.Password,
      })
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<ICustomerAuthResponse>>
        ) => {
          if (response.data.Error) {
            this.error = response.data.Message;
          } else {
            let data = response.data.Data;
            let permissions: Array<IRoleVM> = data.Role ? data.Role : [];
            this.permissions = permissions;
            const userToken: any = jwtDecode(data.AccessToken);
            this.userType = userTypeEnum[userToken.userType];
            if (data.BusinessTypeId !== null) {
              localStorage.setItem(
                "lizera_businessTypeId",
                data.BusinessTypeId?.toString()
              );
            }
            // if(userTypeEnum[userToken.userType] === userTypeEnum[userTypeEnum.Customer])
            //   localStorage.setItem("lizera_store_id", userToken.storeId?.toString());
            localStorage.setItem(
              "lizera_permissions",
              JSON.stringify(permissions)
            );
            localStorage.setItem(
              "lizera_userType",
              userTypeEnum[userToken.userType]
            );
            localStorage.setItem("lizera_email", userToken.customerEmail);
            localStorage.setItem("lizera_userId", userToken.customerId);
            localStorage.setItem("lizera_store_id", userToken.storeId);
            localStorage.setItem("lizera_token", data.AccessToken);
            localStorage.setItem(
              "lizera_blocked_stores",
              JSON.stringify(data.StoreIds)
            );
            localStorage.setItem(
              "lizera_permission_type",
              PermissionType[userToken.permissionType]
            );
            localStorage.setItem("lizera_is_access_tenant", "false");
            localStorage.setItem("lizera_access_tenant_id", userToken.tenantId);
            localStorage.setItem("lizera_access_tenant_name", "");
            this.isAuthenticated = true;
            toast.success(formatMessage("login_successfully"));
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GenerateCustomerAuthCodeService = (data: any) => {
    this.generateCustomerAuthCodeState.inProgress = true;
    let url = URLConstants.GenerateAuthCode;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.generateCustomerAuthCodeState.error = response.data.Message;
        } else {
          if (response.data.Message == "AuthCodeMailSentSuccessfully") {
            this.generateCustomerAuthCodeState.success = true;
          }
        }
      })
      .catch((err: string) => {
        this.generateCustomerAuthCodeState.error = err;
      })
      .finally(
        action(() => {
          this.generateCustomerAuthCodeState.inProgress = false;
        })
      );
  };

  AuthenticateCustomerAuthCodeService = (data: any, setCustomerDetail:any = null) => {
    this.authenticateCustomerAuthCodeState.inProgress = true;
    let url = `${URLConstants.AuthenticateAuthCode}?AuthCode=${data.AuthCode}&AuthType=${data.AuthType}&Email=${data.Email}&FirstNameKana=${data.FirstNameKana}&LastNameKana=${data.LastNameKana}&Phone1=${data.Phone1}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.authenticateCustomerAuthCodeState.error = response.data.Message;
        } else {
          if (response.data.Message == "AuthenticationSuccess") {
            this.isCustomerAuthCodeValidated = true;
            this.authenticateCustomerAuthCodeState.success = true;
            if(setCustomerDetail){
              setCustomerDetail(response.data.Data);
            }
          }
        }
      })
      .catch((err: string) => {
        this.authenticateCustomerAuthCodeState.error = err;
      })
      .finally(
        action(() => {
          this.authenticateCustomerAuthCodeState.inProgress = false;
        })
      );
  };

  setIsCustomerAuthCodeValidatedFalse = () => {
    this.isCustomerAuthCodeValidated = false;
  };

  resetAuthenticateCustomerAuthCode = () => {
    this.authenticateCustomerAuthCodeState = { ...this.initialStateValue };
  };

  resetAuthenticateCustomerAuthCodeError = () => {
    this.authenticateCustomerAuthCodeState.error = "";
  };

  resetGenerateCustomerAuthCodeState = () => {
    this.generateCustomerAuthCodeState = { ...this.initialStateValue };
  };

  ResetPasswordAuthLinkService = (data: any) => {
    this.resetPasswordAuthLinkState.inProgress = true;
    let url = URLConstants.ResetPasswordAuthLink;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.resetPasswordAuthLinkState.error = response.data.Message;
        } else {
          if (response.data.Message == "AuthLinkMailSentSuccessfully") {
            this.isResetPasswordAuthLinkGenerated = true;
            this.resetPasswordAuthLinkState.success = true;
          }
        }
      })
      .catch((err: string) => {
        this.resetPasswordAuthLinkState.error = err;
      })
      .finally(
        action(() => {
          this.resetPasswordAuthLinkState.inProgress = false;
        })
      );
  };

  resetResetPassowrdAuthLinkState = () => {
    this.resetPasswordAuthLinkState = { ...this.initialStateValue };
  };

  resetIsResetPasswordAuthLinkGenerated = () => {
    this.isResetPasswordAuthLinkGenerated = false;
  };

  ResetPasswordService = (data: any) => {
    this.resetPasswordState.inProgress = true;
    let url = URLConstants.ResetCustomerPassword;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.resetPasswordState.error = response.data.Message;
        } else this.resetPasswordState.success = true;
      })
      .catch((err: string) => {
        this.resetPasswordState.error = err;
      })
      .finally(
        action(() => {
          this.resetPasswordState.inProgress = false;
        })
      );
  };

  resetStatePasswordReset = () => {
    this.resetPasswordState = {...this.initialStateValue};
  }
}

export default new AuthStore();
